import { Link } from 'react-router-dom';
import { Trophy, Zap, TrendingUp, Target, Shield } from 'lucide-react';

export default function BlogPolymarketSportsDegens() {
  return (
    <div className="min-h-screen bg-dark-900 text-white py-8 px-4 sm:px-6 lg:px-8">
      <main className="max-w-4xl mx-auto space-y-10 animate-fade-in">
        {/* Header */}
        <header className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-600/15 border border-emerald-500/40 text-xs text-emerald-100 uppercase tracking-wide">
            <Trophy className="w-3 h-3" />
            <span>Polymarket Sports Betting Degens</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
            Polymarket Sports Betting Degens: Copy Winners Instead of Chasing Live Lines
          </h1>
          <p className="text-dark-300 text-sm sm:text-base max-w-2xl">
            If you&apos;re refreshing Polymarket during every NFL drive, NBA run and soccer shootout, you&apos;re
            already a sports betting degen. BetMimic lets you plug directly into the sharpest Polymarket
            sports traders and copy their orders into your own USDC.e wallet with real risk limits.
          </p>
        </header>

        {/* Section: Why Polymarket sports markets are perfect for degens */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary-400" />
            Why Polymarket sports markets are degen heaven
          </h2>
          <p className="text-sm text-dark-300">
            Polymarket sports markets trade like a live exchange, not a dumb sportsbook. Lines move
            instantly when drives change, injuries hit or news drops. That&apos;s great if you&apos;re glued to
            the order book – and terrible if you&apos;re trying to copy good accounts manually.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              <span className="font-medium">Tighter spreads</span> than most off-chain books, but you need
              to hit them on time.
            </li>
            <li>
              <span className="font-medium">In-play movement</span> that rewards people who watch every play
              and know where to reload.
            </li>
            <li>
              <span className="font-medium">Player props and long-tail markets</span> that degens love, but
              are easy to brick if you&apos;re late.
            </li>
          </ul>
        </section>

        {/* Section: Using BetMimic as your Polymarket sports copy trading bot */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary-400" />
            BetMimic as your Polymarket sports copy trading bot
          </h2>
          <p className="text-sm text-dark-300">
            Instead of guessing which screenshots to follow, BetMimic watches real Polymarket sports betting
            wallets and mirrors their trades with the official CLOB client. Think of it as a dedicated
            <strong>Polymarket sports betting copy trading bot</strong> that only cares about actual fills.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              Track specific sports traders or mixed sports + politics degenerates on Polymarket.
            </li>
            <li>
              Build separate copy configs for <span className="font-medium">NFL, NBA, soccer, fight night</span>
              or general degen markets.
            </li>
            <li>
              Size by fixed USDC.e or percentage of your bankroll so you&apos;re never over-levered on one game.
            </li>
          </ul>
        </section>

        {/* Section: Bankroll and tilt control for sports degens */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary-400" />
            Bankroll and tilt control when the game goes sideways
          </h2>
          <p className="text-sm text-dark-300">
            Every sports degen knows the feeling: one bad beat, then you chase, double, triple, and
            suddenly your roll is dust. BetMimic bakes basic bankroll management into the engine so your
            worst tilt session gets capped automatically.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              <span className="font-medium">Daily loss limits</span> on each config – once you hit your
              pain number in USDC.e, it stops copying new trades.
            </li>
            <li>
              <span className="font-medium">Max exposure per sport or trader</span> so you don&apos;t end up
              with your entire bankroll riding on one NBA night.
            </li>
            <li>
              <span className="font-medium">Stop loss / take profit</span> on Polymarket positions with
              a backend monitor that actually closes them.
            </li>
          </ul>
        </section>

        {/* Section: Quick sports-focused setup */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary-400" />
            5-step Polymarket sports copy trading setup
          </h2>
          <ol className="text-sm text-dark-300 list-decimal list-inside space-y-1">
            <li>Fund a Polymarket wallet with USDC.e on Polygon and confirm you can bet manually.</li>
            <li>
              On BetMimic, connect that wallet in <span className="font-medium">Settings → Wallet</span> so
              the engine can talk to the official CLOB.
            </li>
            <li>
              Pick one or two sharp sports accounts from the <Link to="/traders" className="text-primary-300">Traders</Link>{' '}
              leaderboard.
            </li>
            <li>
              Create a "+EV Sports" copy config with conservative max exposure and sane per-game sizing.
            </li>
            <li>
              Let BetMimic mirror their sports trades while you watch games, and review everything later on
              the Positions and Dashboard pages.
            </li>
          </ol>
        </section>

        {/* CTA / internal links */}
        <section className="space-y-3">
          <p className="text-sm text-dark-300">
            BetMimic doesn&apos;t turn bad bets into winners – it just gives Polymarket sports betting degens a
            real copy trading stack with limits, logs and proper PnL. If you&apos;re already sweating games,
            you might as well route that energy through something structured.
          </p>
          <div className="flex flex-wrap gap-3 text-sm">
            <Link
              to="/tutorial"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800 border border-dark-600 hover:border-primary-500 transition-colors"
            >
              <Target className="w-4 h-4" />
              Learn BetMimic Polymarket copy trading basics
            </Link>
            <Link
              to="/blog/polymarket-degen-copy-trading"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600/80 hover:bg-primary-500 transition-colors"
            >
              <Zap className="w-4 h-4" />
              Read the full degen copy trading guide
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
